import { useMemo, useState } from "react";
import {
  ActivityFragment,
  EventFragment,
} from "../../../../../../__generated__/graphql";
import {
  cn,
  formatDateWithDay,
  formatTimeRange,
} from "../../../../../../lib/utils";
import Accordion from "../../../../../../components/Accordion";
import { Icon } from "../../../../../../components/Icon";
import ActivityDetails from "./components/ActivityDetails";
import Button from "../../../../../../components/Button";
import { FaPen } from "react-icons/fa6";
import ActivityModal from "../EventPlanning/components/ActivityModal";
import ParticipantsComponents from "./components/ParticipantsComponents";
import { useUser } from "../../../../../../contexts/UserContext/hooks/useUser";
import { IoDuplicate } from "react-icons/io5";
import { useEventDetails } from "../../../../../../components/EventDetails/context/hook";

export default function Schedule({ event }: { event: EventFragment }) {
  const { currentOrganization } = useUser();
  const { selectedParticipants } = useEventDetails();

  const [selectedActivity, setSelectedActivity] =
    useState<ActivityFragment | null>(null);
  const [duplicateActivity, setDuplicateActivity] =
    useState<ActivityFragment | null>(null);

  // This component could be an issue for events spanning multiple days
  const uniqueDatesOfActivities = useMemo(() => {
    const datesOfActivities = event.activities?.map((activity) => {
      // const date = new Date(activity.startDate);
      return formatDateWithDay(activity.startDate);
    });

    return [...new Set(datesOfActivities)];
  }, [event.activities]);

  const activitiesByDate = (date: string) => {
    const activitiesFound = event.activities?.filter((activity) => {
      // const activityDate = new Date(activity.startDate);
      return formatDateWithDay(activity.startDate) === date;
    });

    if (!activitiesFound) {
      return [];
    }

    return activitiesFound.sort((a, b) => {
      const aDate = new Date(a.startDate);
      const bDate = new Date(b.startDate);
      return aDate.getTime() - bDate.getTime();
    });
  };

  const activityAccordionTitle = (activity: ActivityFragment) => {
    const time = formatTimeRange(activity.startDate, activity.endDate);

    const title = activity.title;

    return (
      <div className="flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Icon icon={activity.icon} className="w-4 h-4" />
          <div className="text-sm text-secondary">{time}</div>
          <div className="text-sm">{title}</div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <ParticipantsComponents activity={activity} />
          </div>
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: activity.color }}
          />
        </div>
      </div>
    );
  };

  if (uniqueDatesOfActivities.length === 0) {
    return (
      <div className="p-4 text-tertiary text-sm text-center">
        Aucune activité pour le moment
      </div>
    );
  }

  return (
    <>
      <ActivityModal
        isOpen={selectedActivity !== null || duplicateActivity !== null}
        onClose={() => {
          setSelectedActivity(null);
          setDuplicateActivity(null);
        }}
        eventId={event.id}
        defaultActivity={selectedActivity || duplicateActivity}
        isDuplicate={duplicateActivity !== null}
      />

      <div className="flex flex-col gap-2">
        {uniqueDatesOfActivities.map((date) => (
          <div
            key={date}
            className="border border-border rounded-md overflow-hidden"
          >
            <div className="flex rounded-md w-full flex-col">
              <div className="text-lg font-bold py-2 px-4 border-b border-border bg-card">
                {date}
              </div>
              <div className="flex flex-col">
                {activitiesByDate(date)?.map((activity, index) => {
                  const isOwner =
                    activity.createdByUserOrganization.organizationId ===
                    currentOrganization?.id;

                  const activityParticipants = activity.activityParticipants;
                  const hasParticipantsSelected = activityParticipants?.some(
                    (participant) =>
                      selectedParticipants.some(
                        (selectedParticipant) =>
                          selectedParticipant.publicUserOrganization.id ===
                          participant.publicUserOrganization.id
                      )
                  );

                  return (
                    <Accordion
                      key={activity.id}
                      title={activityAccordionTitle(activity)}
                      isLast={index === activitiesByDate(date)?.length - 1}
                      className={cn(
                        "px-4 py-2 bg-card transition-all duration-300",
                        !hasParticipantsSelected &&
                          selectedParticipants.length > 0 &&
                          "opacity-20"
                      )}
                    >
                      <div className="px-4 py-2 bg-[#242424] rounded-md">
                        <div className="flex justify-end mb-2">
                          {isOwner ? (
                            <Button
                              variant="secondary"
                              className="text-sm gap-2 items-center text-accent"
                              onClick={() => {
                                setSelectedActivity(null);
                                setDuplicateActivity(activity);
                              }}
                            >
                              <IoDuplicate className="w-4 h-4" />
                              Dupliquer
                            </Button>
                          ) : null}

                          {isOwner ? (
                            <div className="flex justify-end mb-2">
                              <Button
                                variant="secondary"
                                className="flex gap-2 items-center"
                                onClick={() => {
                                  setDuplicateActivity(null);
                                  setSelectedActivity(activity);
                                }}
                              >
                                <FaPen className="w-3 h-3" />
                                <div className="text-sm">Modifier</div>
                              </Button>
                            </div>
                          ) : null}
                        </div>

                        <ActivityDetails activityId={activity.id} />
                      </div>
                    </Accordion>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
